import React from 'react';

export default function NetworkDiagram({ config }) {
  const wans = config.internet?.wans || [];
  const vlans = config.vlans || [];
  const hasDHCP = config.dhcpServers?.length > 0;
  const hasFirewall = !!config.firewall;
  const hasHotspot = !!config.hotspot;
  const hasPortForward = config.portForwards?.length > 0;
  const hasQoS = !!config.qos;

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
        แผนผังเครือข่าย (Network Topology)
      </h3>

      <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 overflow-x-auto">
        <svg viewBox="0 0 800 320" className="w-full min-w-[600px]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="wanGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#1d4ed8" />
            </linearGradient>
            <linearGradient id="routerGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#6366f1" />
              <stop offset="100%" stopColor="#4f46e5" />
            </linearGradient>
            <linearGradient id="lanGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#10b981" />
              <stop offset="100%" stopColor="#059669" />
            </linearGradient>
            <filter id="shadow" x="-10%" y="-10%" width="130%" height="130%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15" />
            </filter>
          </defs>

          {/* Internet Cloud */}
          <ellipse cx="400" cy="35" rx="120" ry="28" fill="#e0f2fe" stroke="#38bdf8" strokeWidth="2" filter="url(#shadow)" />
          <text x="400" y="40" textAnchor="middle" fill="#0369a1" fontSize="14" fontWeight="bold">Internet</text>

          {/* WAN Connections */}
          {wans.length > 0 ? wans.map((wan, i) => {
            const x = 400 + (i - (wans.length - 1) / 2) * 160;
            const colors = ['#3b82f6', '#f59e0b', '#8b5cf6', '#10b981', '#ef4444'];
            const color = colors[i % colors.length];
            return (
              <g key={i}>
                <line x1={x} y1="63" x2={x} y2="100" stroke={color} strokeWidth="2" strokeDasharray="4,3" />
                <rect x={x - 55} y="68" width="110" height="28" rx="6" fill={color} filter="url(#shadow)" />
                <text x={x} y="87" textAnchor="middle" fill="white" fontSize="10" fontWeight="600">{wan.comment || 'WAN-' + (i + 1)}</text>
                <text x={x} y="100" textAnchor="middle" fill="#6b7280" fontSize="8">{wan.type.toUpperCase()}</text>
              </g>
            );
          }) : (
            <g>
              <line x1="400" y1="63" x2="400" y2="100" stroke="#9ca3af" strokeWidth="2" strokeDasharray="4,3" />
              <text x="400" y="85" textAnchor="middle" fill="#9ca3af" fontSize="10">ยังไม่ได้ตั้งค่า WAN</text>
            </g>
          )}

          {/* MikroTik Router */}
          <rect x="300" y="110" width="200" height="70" rx="12" fill="url(#routerGrad)" filter="url(#shadow)" />
          <text x="400" y="138" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">MikroTik Router</text>
          <text x="400" y="156" textAnchor="middle" fill="#c7d2fe" fontSize="10">RouterOS</text>
          <text x="400" y="172" textAnchor="middle" fill="#a5b4fc" fontSize="9">
            {hasFirewall ? 'FW ' : ''}{hasDHCP ? 'DHCP ' : ''}{hasHotspot ? 'HS ' : ''}{hasQoS ? 'QoS ' : ''}
          </text>

          {/* Port Forward indicator */}
          {hasPortForward && (
            <g>
              <rect x="520" y="125" width="60" height="24" rx="6" fill="#f97316" filter="url(#shadow)" />
              <text x="550" y="141" textAnchor="middle" fill="white" fontSize="9" fontWeight="600">PF</text>
              <line x1="500" y1="145" x2="520" y2="137" stroke="#f97316" strokeWidth="1.5" />
            </g>
          )}

          {/* LAN Bridge */}
          <line x1="400" y1="180" x2="400" y2="210" stroke="#10b981" strokeWidth="2" />
          <rect x="330" y="210" width="140" height="32" rx="8" fill="url(#lanGrad)" filter="url(#shadow)" />
          <text x="400" y="231" textAnchor="middle" fill="white" fontSize="11" fontWeight="600">Bridge / LAN</text>

          {/* VLANs */}
          {vlans.length > 0 ? vlans.map((vlan, i) => {
            const vlanX = 150 + i * 170;
            const vlanColors = ['#8b5cf6', '#ec4899', '#f59e0b', '#06b6d4', '#84cc16'];
            const vc = vlanColors[i % vlanColors.length];
            return (
              <g key={i}>
                <line x1="400" y1="242" x2={vlanX + 40} y2="260" stroke={vc} strokeWidth="1.5" strokeDasharray="3,2" />
                <rect x={vlanX - 10} y="246" width="100" height="28" rx="6" fill={vc} filter="url(#shadow)" opacity="0.9" />
                <text x={vlanX + 40} y="264" textAnchor="middle" fill="white" fontSize="9" fontWeight="600">{vlan.name || 'VLAN ' + vlan.id}</text>
              </g>
            );
          }) : (
            <g>
              <rect x="340" y="255" width="120" height="28" rx="6" fill="#e5e7eb" stroke="#d1d5db" strokeWidth="1" />
              <text x="400" y="274" textAnchor="middle" fill="#6b7280" fontSize="10">LAN Network</text>
            </g>
          )}

          {/* Client devices */}
          <g>
            <text x="100" y="270" fill="#9ca3af" fontSize="9">PC</text>
            <text x="100" y="285" fill="#9ca3af" fontSize="9">Phone</text>
            <text x="100" y="300" fill="#9ca3af" fontSize="9">Server</text>
            <line x1="140" y1="270" x2="160" y2="270" stroke="#d1d5db" strokeWidth="1" />
            <line x1="140" y1="285" x2="160" y2="285" stroke="#d1d5db" strokeWidth="1" />
            <line x1="140" y1="300" x2="160" y2="300" stroke="#d1d5db" strokeWidth="1" />
          </g>

          {/* Legend */}
          <g transform="translate(620, 200)">
            <rect x="0" y="0" width="160" height="100" rx="8" fill="white" stroke="#e5e7eb" strokeWidth="1" filter="url(#shadow)" />
            <text x="80" y="18" textAnchor="middle" fill="#374151" fontSize="9" fontWeight="600">Legend</text>
            <line x1="10" y1="30" x2="30" y2="30" stroke="#3b82f6" strokeWidth="2" strokeDasharray="4,3" />
            <text x="35" y="33" fill="#6b7280" fontSize="8">= WAN</text>
            <line x1="10" y1="45" x2="30" y2="45" stroke="#10b981" strokeWidth="2" />
            <text x="35" y="48" fill="#6b7280" fontSize="8">= LAN</text>
            <line x1="10" y1="60" x2="30" y2="60" stroke="#8b5cf6" strokeWidth="1.5" strokeDasharray="3,2" />
            <text x="35" y="63" fill="#6b7280" fontSize="8">= VLAN</text>
            <rect x="10" y="72" width="20" height="10" rx="3" fill="#6366f1" />
            <text x="35" y="81" fill="#6b7280" fontSize="8">= Router</text>
          </g>
        </svg>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
        <StatCard icon="🌐" label="WAN" value={wans.length + ' ตัว'} />
        <StatCard icon="🔀" label="VLAN" value={vlans.length + ' ตัว'} />
        <StatCard icon="📡" label="DHCP" value={(config.dhcpServers?.length || 0) + ' ตัว'} />
        <StatCard icon="🔌" label="Port Fwd" value={(config.portForwards?.length || 0) + ' กฎ'} />
      </div>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2 text-center">
      <div className="text-lg">{icon}</div>
      <div className="text-xs font-semibold text-gray-900 dark:text-white">{value}</div>
      <div className="text-[10px] text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  );
}
